/**
 * CampaignOficialHelpDialog - Modal educativo interativo
 * 
 * Explica a estrutura de campanhas para WhatsApp Oficial com:
 * - Navegação por seções (tabs)
 * - Exemplos visuais com previews simulados
 * - Animações para guiar o usuário
 * - 5+ exemplos práticos variados
 * 
 * @author Sistema de Campanhas UaizyChat
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import {
  FileText,
  MessageSquare,
  MousePointerClick,
  Variable,
  Lightbulb,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Phone,
  CreditCard,
  ArrowRight,
  Sparkles,
  AlertTriangle,
  FileSpreadsheet,
  ChevronRight,
  Package,
  Truck,
  Receipt,
  Gift,
  Calendar,
} from 'lucide-react';

// ============================================================================
// TIPOS
// ============================================================================

interface CampaignOficialHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

/**
 * Preview visual de um template WhatsApp
 */
function TemplatePreview({ 
  header,
  body, 
  footer, 
  buttons,
  className 
}: { 
  header?: { type: 'text' | 'image'; content: string };
  body: string;
  footer?: string;
  buttons?: Array<{ text: string; icon: React.ReactNode }>;
  className?: string;
}) {
  return (
    <div className={cn(
      "bg-[#e5ddd5] rounded-lg p-4 max-w-xs mx-auto",
      className
    )}>
      <div className="bg-background rounded-lg shadow-sm overflow-hidden">
        {/* Header */}
        {header && (
          <div className="border-b">
            {header.type === 'image' ? (
              <div className="h-32 bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
                <Package className="h-10 w-10 text-primary/60" />
              </div>
            ) : (
              <div className="px-3 py-2 bg-muted/50 text-sm font-medium">
                {header.content}
              </div>
            )}
          </div>
        )}
        
        {/* Body */}
        <div className="p-3 text-sm whitespace-pre-line">
          {body}
        </div>
        
        {/* Footer */}
        {footer && (
          <div className="px-3 pb-2 text-xs text-muted-foreground">
            {footer}
          </div>
        )}
        
        {/* Buttons */}
        {buttons && buttons.length > 0 && (
          <div className="border-t divide-y">
            {buttons.map((btn, idx) => (
              <button
                key={idx}
                className="w-full px-3 py-2 text-sm text-primary font-medium flex items-center justify-center gap-2 hover:bg-muted/50 transition-colors"
              >
                {btn.icon}
                {btn.text}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Timestamp */}
      <div className="flex justify-end mt-1">
        <span className="text-[10px] text-muted-foreground">12:34</span>
      </div>
    </div>
  );
}

/**
 * Card de comparação (permitido vs bloqueado)
 */
function ComparisonCard({ 
  allowed, 
  title, 
  description 
}: { 
  allowed: boolean;
  title: string;
  description: string;
}) {
  return (
    <div className={cn(
      "p-4 rounded-lg border-2 transition-all",
      allowed 
        ? "border-green-500/50 bg-green-500/5" 
        : "border-red-500/50 bg-red-500/5"
    )}>
      <div className="flex items-center gap-2 mb-2">
        {allowed ? (
          <CheckCircle2 className="h-5 w-5 text-green-500" />
        ) : (
          <XCircle className="h-5 w-5 text-red-500" />
        )}
        <span className="font-semibold">{title}</span>
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

/**
 * Bloco de exemplo com código
 */
function ExampleBlock({ 
  icon: Icon, 
  title, 
  children 
}: { 
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-muted/50 border-b">
        <Icon className="h-4 w-4 text-primary" />
        <span className="font-medium text-sm">{title}</span>
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function CampaignOficialHelpDialog({
  open,
  onOpenChange,
}: CampaignOficialHelpDialogProps) {
  const [activeTab, setActiveTab] = useState('intro');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 gap-0">
        {/* Header fixo */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <span className="text-xl">Como Configurar Campanhas</span>
              <Badge variant="secondary" className="ml-2">WhatsApp Oficial</Badge>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Tabs de navegação */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <div className="px-6 pt-4 shrink-0">
            <TabsList className="grid w-full grid-cols-5 h-auto">
              <TabsTrigger value="intro" className="flex flex-col gap-1 py-2 h-auto">
                <FileText className="h-4 w-4" />
                <span className="text-xs">Introdução</span>
              </TabsTrigger>
              <TabsTrigger value="components" className="flex flex-col gap-1 py-2 h-auto">
                <MessageSquare className="h-4 w-4" />
                <span className="text-xs">Componentes</span>
              </TabsTrigger>
              <TabsTrigger value="buttons" className="flex flex-col gap-1 py-2 h-auto">
                <MousePointerClick className="h-4 w-4" />
                <span className="text-xs">Botões</span>
              </TabsTrigger>
              <TabsTrigger value="variables" className="flex flex-col gap-1 py-2 h-auto">
                <Variable className="h-4 w-4" />
                <span className="text-xs">Variáveis</span>
              </TabsTrigger>
              <TabsTrigger value="examples" className="flex flex-col gap-1 py-2 h-auto">
                <Lightbulb className="h-4 w-4" />
                <span className="text-xs">Exemplos</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Conteúdo com scroll */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-6 py-4">
              {/* TAB: INTRODUÇÃO */}
              <TabsContent value="intro" className="mt-0 space-y-6">
                {/* Alerta importante */}
                <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-700 dark:text-amber-400 mb-1">
                      Templates são Obrigatórios
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      No WhatsApp Oficial (API), você <strong>não pode</strong> enviar mensagens 
                      livres para iniciar conversas. É necessário usar templates pré-aprovados pela Meta.
                    </p>
                  </div>
                </div>

                {/* Comparação visual */}
                <div>
                  <h3 className="font-semibold mb-3">Diferença na Prática</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ComparisonCard
                      allowed={false}
                      title="Sem Template"
                      description="Enviar 'Olá, tudo bem?' diretamente → BLOQUEADO pela API"
                    />
                    <ComparisonCard
                      allowed={true}
                      title="Com Template"
                      description="Usar template aprovado com variáveis → PERMITIDO e entregue"
                    />
                  </div>
                </div>

                {/* Janela 24h */}
                <div>
                  <h3 className="font-semibold mb-3">Regra da Janela de 24 Horas</h3>
                  <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        1
                      </div>
                      <p className="text-sm">Você envia um <strong>template</strong> para o contato</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        2
                      </div>
                      <p className="text-sm">Se ele responder, abre uma <strong>janela de 24 horas</strong></p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        3
                      </div>
                      <p className="text-sm">Dentro da janela, você pode enviar <strong>mensagens livres</strong></p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                        4
                      </div>
                      <p className="text-sm">Após 24h sem resposta, volta a precisar de <strong>template</strong></p>
                    </div>
                  </div>
                </div>

                {/* CTA próximo */}
                <div className="flex justify-end">
                  <Button onClick={() => setActiveTab('components')} variant="outline">
                    Próximo: Componentes do Template
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* TAB: COMPONENTES */}
              <TabsContent value="components" className="mt-0 space-y-6">
                <p className="text-muted-foreground">
                  Um template é composto por até 4 partes. Veja cada uma:
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Explicação */}
                  <div className="space-y-4">
                    <ExampleBlock icon={FileText} title="📷 HEADER (Opcional)">
                      <p className="text-sm text-muted-foreground mb-2">
                        Primeira linha ou mídia do template.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline">Texto</Badge>
                        <Badge variant="outline">Imagem</Badge>
                        <Badge variant="outline">Vídeo</Badge>
                        <Badge variant="outline">Documento</Badge>
                      </div>
                    </ExampleBlock>

                    <ExampleBlock icon={MessageSquare} title="📝 BODY (Obrigatório)">
                      <p className="text-sm text-muted-foreground mb-2">
                        Texto principal com variáveis <code className="bg-muted px-1 rounded">{'{{1}}'}</code>, 
                        <code className="bg-muted px-1 rounded ml-1">{'{{2}}'}</code>...
                      </p>
                      <p className="text-xs text-muted-foreground">Máximo: 1024 caracteres</p>
                    </ExampleBlock>

                    <ExampleBlock icon={FileText} title="📎 FOOTER (Opcional)">
                      <p className="text-sm text-muted-foreground">
                        Texto fixo no rodapé. Sem variáveis. Máx: 60 caracteres.
                      </p>
                    </ExampleBlock>

                    <ExampleBlock icon={MousePointerClick} title="🔘 BUTTONS (Opcional)">
                      <p className="text-sm text-muted-foreground mb-2">
                        Botões interativos. Máximo: 3 botões.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Badge>URL</Badge>
                        <Badge>Quick Reply</Badge>
                        <Badge>Telefone</Badge>
                        <Badge>PIX</Badge>
                      </div>
                    </ExampleBlock>
                  </div>

                  {/* Preview */}
                  <div>
                    <p className="text-sm font-medium mb-3 text-center">Preview Visual</p>
                    <TemplatePreview
                      header={{ type: 'image', content: '' }}
                      body={`Olá {{1}}! 🎉\n\nSeu pedido {{2}} foi confirmado!\n\n📦 Produto: {{3}}\n💰 Valor: R$ {{4}}`}
                      footer="Atenciosamente, Equipe Loja"
                      buttons={[
                        { text: 'Ver Pedido', icon: <ExternalLink className="h-3 w-3" /> },
                        { text: 'Pagar com PIX', icon: <CreditCard className="h-3 w-3" /> },
                      ]}
                    />
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button onClick={() => setActiveTab('intro')} variant="ghost">
                    Voltar
                  </Button>
                  <Button onClick={() => setActiveTab('buttons')} variant="outline">
                    Próximo: Tipos de Botões
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* TAB: BOTÕES */}
              <TabsContent value="buttons" className="mt-0 space-y-6">
                <p className="text-muted-foreground">
                  Botões tornam seu template interativo. Conheça cada tipo:
                </p>

                <div className="grid gap-4">
                  {/* URL Estática */}
                  <div className="p-4 rounded-lg border bg-card">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <ExternalLink className="h-4 w-4 text-blue-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold">URL Estática</h4>
                        <p className="text-xs text-muted-foreground">Link fixo para todos os destinatários</p>
                      </div>
                    </div>
                    <code className="text-xs bg-muted px-2 py-1 rounded block">
                      https://meusite.com.br/promocao
                    </code>
                  </div>

                  {/* URL Dinâmica */}
                  <div className="p-4 rounded-lg border bg-card border-primary/50">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <ExternalLink className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold">URL Dinâmica</h4>
                        <Badge variant="secondary" className="ml-2">Mais usado</Badge>
                        <p className="text-xs text-muted-foreground">Link personalizado com variável do CSV</p>
                      </div>
                    </div>
                    <code className="text-xs bg-muted px-2 py-1 rounded block mb-2">
                      https://loja.com/pedido/<span className="text-primary font-bold">{'{{1}}'}</span>
                    </code>
                    <p className="text-xs text-muted-foreground">
                      <ArrowRight className="h-3 w-3 inline mr-1" />
                      <code className="bg-muted px-1 rounded">{'{{1}}'}</code> é substituído pela coluna 
                      <code className="bg-muted px-1 rounded ml-1">url_suffix</code> do CSV
                    </p>
                  </div>

                  {/* Quick Reply */}
                  <div className="p-4 rounded-lg border bg-card">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-8 w-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                        <MessageSquare className="h-4 w-4 text-green-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Quick Reply</h4>
                        <p className="text-xs text-muted-foreground">Envia o texto do botão como resposta</p>
                      </div>
                    </div>
                    <p className="text-sm">Ex: "Confirmar Recebimento" → Ao clicar, envia essa mensagem</p>
                  </div>

                  {/* Telefone */}
                  <div className="p-4 rounded-lg border bg-card">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="h-8 w-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <Phone className="h-4 w-4 text-purple-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Telefone</h4>
                        <p className="text-xs text-muted-foreground">Inicia chamada ao clicar</p>
                      </div>
                    </div>
                    <code className="text-xs bg-muted px-2 py-1 rounded block">
                      +5511999999999
                    </code>
                  </div>

                  {/* PIX / ORDER_DETAILS */}
                  <div className="p-4 rounded-lg border-2 border-amber-500/50 bg-amber-500/5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                        <CreditCard className="h-4 w-4 text-amber-500" />
                      </div>
                      <div>
                        <h4 className="font-semibold">PIX (ORDER_DETAILS)</h4>
                        <Badge className="bg-amber-500">Pagamento</Badge>
                        <p className="text-xs text-muted-foreground">Botão de pagamento PIX integrado</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-background p-2 rounded">
                        <span className="text-muted-foreground">Código PIX:</span>
                        <span className="block font-mono truncate">00020126...</span>
                      </div>
                      <div className="bg-background p-2 rounded">
                        <span className="text-muted-foreground">Valor:</span>
                        <span className="block font-bold">R$ 150,00</span>
                      </div>
                      <div className="bg-background p-2 rounded">
                        <span className="text-muted-foreground">Recebedor:</span>
                        <span className="block">Minha Loja</span>
                      </div>
                      <div className="bg-background p-2 rounded">
                        <span className="text-muted-foreground">Chave:</span>
                        <span className="block font-mono text-[10px]">12.345.678/0001-90</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button onClick={() => setActiveTab('components')} variant="ghost">
                    Voltar
                  </Button>
                  <Button onClick={() => setActiveTab('variables')} variant="outline">
                    Próximo: Sistema de Variáveis
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* TAB: VARIÁVEIS */}
              <TabsContent value="variables" className="mt-0 space-y-6">
                <p className="text-muted-foreground">
                  Variáveis permitem personalizar cada mensagem com dados do CSV.
                </p>

                {/* Fluxo visual */}
                <div className="p-4 rounded-lg border bg-muted/30">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    Como funciona o mapeamento
                  </h4>
                  
                  <div className="flex items-center gap-4 overflow-x-auto pb-2">
                    {/* CSV */}
                    <div className="shrink-0 p-3 rounded-lg border bg-card min-w-[200px]">
                      <p className="text-xs font-medium mb-2">📄 Seu CSV</p>
                      <div className="space-y-1 text-xs font-mono">
                        <div className="flex gap-2">
                          <span className="text-muted-foreground">nome:</span>
                          <span>João</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-muted-foreground">variavel1:</span>
                          <span>#12345</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-muted-foreground">pix_amount:</span>
                          <span>150.00</span>
                        </div>
                      </div>
                    </div>

                    <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />

                    {/* Seletor */}
                    <div className="shrink-0 p-3 rounded-lg border-2 border-primary bg-primary/5 min-w-[200px]">
                      <p className="text-xs font-medium mb-2">⚙️ Você seleciona</p>
                      <div className="space-y-1 text-xs">
                        <div className="flex items-center gap-2">
                          <code className="bg-muted px-1 rounded">{'{{1}}'}</code>
                          <span>→</span>
                          <Badge variant="outline">nome</Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="bg-muted px-1 rounded">{'{{2}}'}</code>
                          <span>→</span>
                          <Badge variant="outline">variavel1</Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>PIX</span>
                          <span>→</span>
                          <Badge variant="outline">pix_amount</Badge>
                        </div>
                      </div>
                    </div>

                    <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />

                    {/* Resultado */}
                    <div className="shrink-0 p-3 rounded-lg border bg-green-500/10 border-green-500/30 min-w-[200px]">
                      <p className="text-xs font-medium mb-2">✅ Resultado</p>
                      <div className="text-xs">
                        <p>"Olá <strong>João</strong>!</p>
                        <p>Pedido <strong>#12345</strong></p>
                        <p>Valor: R$ <strong>150,00</strong>"</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tipos de variáveis */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg border bg-card">
                    <h4 className="font-semibold mb-2 text-sm">Variáveis do BODY</h4>
                    <div className="space-y-1">
                      <code className="text-xs bg-muted px-2 py-0.5 rounded block">{'{{1}}'} → variavel1</code>
                      <code className="text-xs bg-muted px-2 py-0.5 rounded block">{'{{2}}'} → variavel2</code>
                      <code className="text-xs bg-muted px-2 py-0.5 rounded block">{'{{3}}'} → nome</code>
                    </div>
                  </div>

                  <div className="p-4 rounded-lg border bg-card">
                    <h4 className="font-semibold mb-2 text-sm">Variáveis de URL</h4>
                    <div className="space-y-1">
                      <code className="text-xs bg-muted px-2 py-0.5 rounded block">{'{{1}}'} → url_suffix</code>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Usado em botões de URL dinâmica
                    </p>
                  </div>

                  <div className="p-4 rounded-lg border bg-card">
                    <h4 className="font-semibold mb-2 text-sm">Variáveis PIX</h4>
                    <div className="space-y-1">
                      <code className="text-xs bg-muted px-2 py-0.5 rounded block">pix_code</code>
                      <code className="text-xs bg-muted px-2 py-0.5 rounded block">pix_amount</code>
                      <code className="text-xs bg-muted px-2 py-0.5 rounded block">pix_key</code>
                    </div>
                  </div>
                </div>

                {/* Dica */}
                <div className="flex items-start gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <Lightbulb className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-primary mb-1">Dica</h4>
                    <p className="text-sm text-muted-foreground">
                      Use o modelo CSV para WhatsApp Oficial que já inclui todas as colunas 
                      necessárias. Baixe em <strong>Listas → Baixar Modelo → Modelo WhatsApp Oficial</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button onClick={() => setActiveTab('buttons')} variant="ghost">
                    Voltar
                  </Button>
                  <Button onClick={() => setActiveTab('examples')} variant="outline">
                    Próximo: Exemplos Práticos
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </TabsContent>

              {/* TAB: EXEMPLOS */}
              <TabsContent value="examples" className="mt-0 space-y-6">
                <p className="text-muted-foreground">
                  Veja exemplos reais de campanhas configuradas:
                </p>

                {/* Exemplo 1: Confirmação de Pedido */}
                <div className="rounded-lg border overflow-hidden">
                  <div className="px-4 py-3 bg-muted/50 border-b flex items-center gap-2">
                    <Package className="h-4 w-4 text-primary" />
                    <span className="font-semibold">Exemplo 1: Confirmação de Pedido</span>
                  </div>
                  <div className="p-4 grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium mb-2 text-muted-foreground">TEMPLATE</p>
                      <div className="p-3 rounded bg-muted/50 text-sm">
                        Olá {'{{1}}'}! 🎉<br/><br/>
                        Seu pedido {'{{2}}'} foi confirmado!<br/>
                        Previsão de entrega: {'{{3}}'}<br/><br/>
                        Obrigado pela preferência!
                      </div>
                    </div>
                    <TemplatePreview
                      body={`Olá João! 🎉\n\nSeu pedido #12345 foi confirmado!\nPrevisão de entrega: 20/12/2024\n\nObrigado pela preferência!`}
                      buttons={[
                        { text: 'Rastrear Pedido', icon: <ExternalLink className="h-3 w-3" /> },
                      ]}
                    />
                  </div>
                </div>

                {/* Exemplo 2: Cobrança PIX */}
                <div className="rounded-lg border-2 border-amber-500/30 overflow-hidden">
                  <div className="px-4 py-3 bg-amber-500/10 border-b flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-amber-600" />
                    <span className="font-semibold">Exemplo 2: Cobrança com PIX</span>
                    <Badge className="bg-amber-500 ml-auto">ORDER_DETAILS</Badge>
                  </div>
                  <div className="p-4 grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium mb-2 text-muted-foreground">TEMPLATE</p>
                      <div className="p-3 rounded bg-muted/50 text-sm">
                        Olá {'{{1}}'}!<br/><br/>
                        Sua fatura está disponível:<br/>
                        📋 Ref: {'{{2}}'}<br/>
                        💰 Valor: R$ {'{{3}}'}<br/><br/>
                        Clique para pagar com PIX!
                      </div>
                      <p className="text-xs font-medium mt-3 mb-2 text-muted-foreground">CSV</p>
                      <code className="text-[10px] bg-muted p-2 rounded block overflow-x-auto">
                        nome,variavel1,variavel2,pix_code,pix_amount,...
                      </code>
                    </div>
                    <TemplatePreview
                      body={`Olá Maria!\n\nSua fatura está disponível:\n📋 Ref: FAT-2024-001\n💰 Valor: R$ 150,00\n\nClique para pagar com PIX!`}
                      buttons={[
                        { text: 'Pagar com PIX', icon: <CreditCard className="h-3 w-3" /> },
                      ]}
                    />
                  </div>
                </div>

                {/* Exemplo 3: Promoção com Link */}
                <div className="rounded-lg border overflow-hidden">
                  <div className="px-4 py-3 bg-muted/50 border-b flex items-center gap-2">
                    <Gift className="h-4 w-4 text-pink-500" />
                    <span className="font-semibold">Exemplo 3: Promoção com Link Dinâmico</span>
                  </div>
                  <div className="p-4 grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium mb-2 text-muted-foreground">TEMPLATE</p>
                      <div className="p-3 rounded bg-muted/50 text-sm">
                        🎁 {'{{1}}'}, oferta exclusiva!<br/><br/>
                        Produto: {'{{2}}'}<br/>
                        Desconto: {'{{3}}'}% OFF<br/><br/>
                        Acesse sua oferta:
                      </div>
                      <p className="text-xs font-medium mt-3 mb-2 text-muted-foreground">URL DINÂMICA</p>
                      <code className="text-xs bg-muted p-2 rounded block">
                        https://loja.com/promo/<span className="text-primary">{'{{1}}'}</span>
                      </code>
                    </div>
                    <TemplatePreview
                      body={`🎁 João, oferta exclusiva!\n\nProduto: Smartphone XYZ\nDesconto: 30% OFF\n\nAcesse sua oferta:`}
                      buttons={[
                        { text: 'Ver Oferta', icon: <ExternalLink className="h-3 w-3" /> },
                      ]}
                    />
                  </div>
                </div>

                {/* Exemplo 4: Aviso de Entrega */}
                <div className="rounded-lg border overflow-hidden">
                  <div className="px-4 py-3 bg-muted/50 border-b flex items-center gap-2">
                    <Truck className="h-4 w-4 text-blue-500" />
                    <span className="font-semibold">Exemplo 4: Aviso de Entrega</span>
                  </div>
                  <div className="p-4 grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium mb-2 text-muted-foreground">TEMPLATE</p>
                      <div className="p-3 rounded bg-muted/50 text-sm">
                        📦 Seu pedido está a caminho!<br/><br/>
                        Olá {'{{1}}'}, o pedido {'{{2}}'} saiu para entrega.<br/><br/>
                        Rastreamento: {'{{3}}'}
                      </div>
                    </div>
                    <TemplatePreview
                      body={`📦 Seu pedido está a caminho!\n\nOlá Carlos, o pedido #98765 saiu para entrega.\n\nRastreamento: BR123456789`}
                      buttons={[
                        { text: 'Rastrear', icon: <ExternalLink className="h-3 w-3" /> },
                        { text: 'Confirmar Recebimento', icon: <MessageSquare className="h-3 w-3" /> },
                      ]}
                    />
                  </div>
                </div>

                {/* Exemplo 5: Lembrete de Agendamento */}
                <div className="rounded-lg border overflow-hidden">
                  <div className="px-4 py-3 bg-muted/50 border-b flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-green-500" />
                    <span className="font-semibold">Exemplo 5: Lembrete de Agendamento</span>
                  </div>
                  <div className="p-4 grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium mb-2 text-muted-foreground">TEMPLATE</p>
                      <div className="p-3 rounded bg-muted/50 text-sm">
                        📅 Lembrete de Consulta<br/><br/>
                        Olá {'{{1}}'}!<br/>
                        Sua consulta está marcada para:<br/>
                        📆 {'{{2}}'} às {'{{3}}'}<br/><br/>
                        Confirme sua presença:
                      </div>
                    </div>
                    <TemplatePreview
                      body={`📅 Lembrete de Consulta\n\nOlá Ana!\nSua consulta está marcada para:\n📆 18/12/2024 às 14:30\n\nConfirme sua presença:`}
                      footer="Clínica Saúde & Bem-Estar"
                      buttons={[
                        { text: 'Confirmar', icon: <CheckCircle2 className="h-3 w-3" /> },
                        { text: 'Reagendar', icon: <Calendar className="h-3 w-3" /> },
                        { text: 'Ligar', icon: <Phone className="h-3 w-3" /> },
                      ]}
                    />
                  </div>
                </div>

                <Separator />

                {/* CTA final */}
                <div className="text-center space-y-3">
                  <p className="text-muted-foreground">
                    Pronto para criar sua campanha?
                  </p>
                  <Button onClick={() => onOpenChange(false)} size="lg">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Começar a Configurar
                  </Button>
                </div>
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export default CampaignOficialHelpDialog;
